import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { companyAPI, clientsAPI } from '../services/api';
import { Printer, Download, X, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const InvoiceViewDialog = ({ open, onOpenChange, invoice }) => {
  const [companySettings, setCompanySettings] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    if (open && invoice) {
      fetchCompanySettings();
      fetchClientData();
    }
  }, [open, invoice]);

  const fetchCompanySettings = async () => {
    try {
      setLoading(true);
      const response = await companyAPI.getSettings();
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Error fetching company settings:', error);
      setCompanySettings({ name: 'Mantra CRM' });
    } finally {
      setLoading(false);
    }
  };

  const fetchClientData = async () => {
    if (!invoice?.clientId) return;
    try {
      const response = await clientsAPI.getById(invoice.clientId);
      setClientData(response.data);
    } catch (error) {
      console.error('Error fetching client data:', error);
    }
  };

  const formatClientAddress = (address) => {
    if (!address) return null;
    const parts = [];
    if (address.line1) parts.push(address.line1);
    if (address.line2) parts.push(address.line2);
    const cityLine = [address.city, address.province, address.postalCode].filter(Boolean).join(', ');
    if (cityLine) parts.push(cityLine);
    if (address.country) parts.push(address.country);
    return parts;
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice?.invoiceNumber || ''}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
          .logo { max-height: 60px; max-width: 200px; }
          .company-name { font-size: 24px; font-weight: bold; color: #1f2937; }
          .company-info { color: #6b7280; font-size: 14px; line-height: 1.6; }
          .invoice-title { font-size: 32px; font-weight: bold; color: #1f2937; text-align: right; }
          .invoice-number { font-size: 14px; color: #6b7280; text-align: right; }
          .client-section { margin: 30px 0; padding: 20px; background: #f9fafb; border-radius: 8px; }
          .section-title { font-size: 12px; color: #6b7280; text-transform: uppercase; margin-bottom: 8px; }
          .client-name { font-size: 18px; font-weight: 600; color: #1f2937; }
          .client-address { font-size: 14px; color: #4b5563; margin-top: 4px; line-height: 1.5; }
          .dates { display: flex; gap: 40px; margin: 20px 0; }
          .date-item { }
          .date-label { font-size: 12px; color: #6b7280; }
          .date-value { font-size: 14px; font-weight: 500; color: #1f2937; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th { background: #f3f4f6; padding: 12px; text-align: left; font-size: 12px; text-transform: uppercase; color: #6b7280; border-bottom: 2px solid #e5e7eb; }
          td { padding: 12px; border-bottom: 1px solid #e5e7eb; font-size: 14px; }
          .amount { text-align: right; }
          .totals { margin-top: 30px; display: flex; justify-content: flex-end; }
          .totals-table { width: 320px; }
          .totals-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
          .totals-row.grand { font-size: 18px; font-weight: bold; border-top: 2px solid #1f2937; border-bottom: none; padding-top: 12px; }
          .status { display: inline-block; padding: 4px 12px; border-radius: 9999px; font-size: 12px; font-weight: 500; }
          .status-paid { background: #d1fae5; color: #065f46; }
          .status-pending { background: #fef3c7; color: #92400e; }
          .status-partial { background: #ffedd5; color: #9a3412; }
          .text-green-600 { color: #059669; }
          .text-yellow-800 { color: #92400e; }
          .bg-yellow-50 { background: #fefce8; padding: 8px; border-radius: 4px; margin: 0 -8px; }
          .bg-green-50 { background: #f0fdf4; padding: 8px; border-radius: 4px; margin: 0 -8px; }
          .payment-history { margin-top: 32px; padding-top: 24px; border-top: 1px solid #e5e7eb; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleDownload = async () => {
    const printContent = printRef.current;
    if (!printContent) return;

    setDownloading(true);
    try {
      // Capture the invoice content as a canvas with lower scale for smaller file size
      const canvas = await html2canvas(printContent, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 0
      });

      // Calculate PDF dimensions (A4 size)
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Create PDF with compression
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      // Convert to JPEG with quality setting for smaller file size
      const imgData = canvas.toDataURL('image/jpeg', 0.7);

      // Add image to PDF (handle multi-page if content is long)
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Save with invoice number as filename
      const filename = `${invoice?.invoiceNumber || 'invoice'}.pdf`;
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Invoice {invoice.invoiceNumber}</DialogTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2">
                <Printer className="h-4 w-4" />
                Print
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownload} disabled={downloading} className="gap-2">
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {downloading ? 'Generating...' : 'Download'}
              </Button>
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <div ref={printRef} className="p-6 bg-white">
            {/* Header with Logo and Invoice Title */}
            <div className="header flex justify-between items-start mb-8">
              <div>
                {companySettings?.logo && (
                  <img 
                    src={companySettings.logo} 
                    alt="Company Logo" 
                    className="logo max-h-16 max-w-[200px] object-contain mb-2"
                  />
                )}
                <div className="company-name text-2xl font-bold text-gray-900 mb-2">
                  {companySettings?.name || 'Mantra CRM'}
                </div>
                {companySettings?.address && (
                  <div className="company-info text-sm text-gray-500 whitespace-pre-line">
                    {companySettings.address}
                  </div>
                )}
                {companySettings?.phone && (
                  <div className="company-info text-sm text-gray-500">
                    Phone: {companySettings.phone}
                  </div>
                )}
                {companySettings?.email && (
                  <div className="company-info text-sm text-gray-500">
                    Email: {companySettings.email}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="invoice-title text-3xl font-bold text-gray-900">INVOICE</div>
                <div className="invoice-number text-gray-500 mt-1">#{invoice.invoiceNumber}</div>
                <div className="mt-2">
                  <span className={`status inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    invoice.status === 'paid' 
                      ? 'status-paid bg-green-100 text-green-800' 
                      : invoice.status === 'partial'
                        ? 'bg-orange-100 text-orange-800'
                        : 'status-pending bg-yellow-100 text-yellow-800'
                  }`}>
                    {invoice.status === 'partial' ? 'PARTIALLY PAID' : invoice.status?.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            {/* Client Info */}
            <div className="client-section bg-gray-50 p-5 rounded-lg mb-6">
              <div className="section-title text-xs text-gray-500 uppercase tracking-wide mb-2">Bill To</div>
              <div className="client-name text-lg font-semibold text-gray-900">{invoice.clientName}</div>
              {clientData?.address && (
                <div className="client-address text-sm text-gray-600 mt-2">
                  {clientData.address.line1 && <div>{clientData.address.line1}</div>}
                  {clientData.address.line2 && <div>{clientData.address.line2}</div>}
                  {(clientData.address.city || clientData.address.province || clientData.address.postalCode) && (
                    <div>
                      {[clientData.address.city, clientData.address.province, clientData.address.postalCode]
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                  )}
                  {clientData.address.country && <div>{clientData.address.country}</div>}
                </div>
              )}
              {clientData?.email && (
                <div className="text-sm text-gray-500 mt-1">{clientData.email}</div>
              )}
            </div>

            {/* Dates */}
            <div className="dates flex gap-10 mb-6">
              <div className="date-item">
                <div className="date-label text-xs text-gray-500 uppercase">Invoice Date</div>
                <div className="date-value font-medium text-gray-900">
                  {invoice.invoiceDate ? format(new Date(invoice.invoiceDate), 'MMMM dd, yyyy') : 'N/A'}
                </div>
              </div>
              <div className="date-item">
                <div className="date-label text-xs text-gray-500 uppercase">Due Date</div>
                <div className="date-value font-medium text-gray-900">
                  {invoice.dueDate ? format(new Date(invoice.dueDate), 'MMMM dd, yyyy') : 'N/A'}
                </div>
              </div>
              {invoice.paidDate && (
                <div className="date-item">
                  <div className="date-label text-xs text-gray-500 uppercase">Paid Date</div>
                  <div className="date-value font-medium text-green-600">
                    {format(new Date(invoice.paidDate), 'MMMM dd, yyyy')}
                  </div>
                </div>
              )}
            </div>

            {/* Line Items Table */}
            <table className="w-full border-collapse mb-6">
              <thead>
                <tr>
                  <th className="bg-gray-100 p-3 text-left text-xs uppercase text-gray-500 border-b-2 border-gray-200">Service</th>
                  <th className="bg-gray-100 p-3 text-left text-xs uppercase text-gray-500 border-b-2 border-gray-200">Year</th>
                  <th className="bg-gray-100 p-3 text-left text-xs uppercase text-gray-500 border-b-2 border-gray-200">Description</th>
                  <th className="bg-gray-100 p-3 text-right text-xs uppercase text-gray-500 border-b-2 border-gray-200">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.lineItems || []).map((item, index) => (
                  <tr key={index}>
                    <td className="p-3 border-b border-gray-200">{item.service || '-'}</td>
                    <td className="p-3 border-b border-gray-200">{item.year || '-'}</td>
                    <td className="p-3 border-b border-gray-200">{item.description || '-'}</td>
                    <td className="p-3 border-b border-gray-200 text-right amount">${(item.amount || 0).toFixed(2)}</td>
                  </tr>
                ))}
                {(!invoice.lineItems || invoice.lineItems.length === 0) && (
                  <tr>
                    <td colSpan={4} className="p-3 text-center text-gray-500">No line items</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Totals */}
            <div className="totals flex justify-end">
              <div className="totals-table w-80">
                <div className="totals-row flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">${(invoice.subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="totals-row flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">GST/HST ({((invoice.hstRate || 0) * 100).toFixed(0)}%)</span>
                  <span className="font-medium">${(invoice.hstAmount || 0).toFixed(2)}</span>
                </div>
                <div className="totals-row grand flex justify-between py-3 border-t-2 border-gray-900 font-bold text-lg">
                  <span>Grand Total</span>
                  <span>${(invoice.grandTotal || 0).toFixed(2)}</span>
                </div>
                
                {/* Payment Information */}
                {(invoice.paidAmount > 0 || invoice.status === 'paid' || invoice.status === 'partial') && (
                  <>
                    <div className="mt-4 pt-4 border-t-2 border-gray-300">
                      <div className="text-xs uppercase text-gray-500 font-semibold mb-2">Payment Summary</div>
                    </div>
                    <div className="totals-row flex justify-between py-2 border-b border-gray-200">
                      <span className="text-green-600">Amount Paid</span>
                      <span className="font-medium text-green-600">
                        ${(invoice.paidAmount || (invoice.status === 'paid' ? invoice.grandTotal : 0) || 0).toFixed(2)}
                      </span>
                    </div>
                    {invoice.status !== 'paid' && (
                      <div className="totals-row flex justify-between py-2 bg-yellow-50 px-2 -mx-2 rounded">
                        <span className="text-yellow-800 font-semibold">Balance Due</span>
                        <span className="font-bold text-yellow-800">
                          ${((invoice.grandTotal || 0) - (invoice.paidAmount || 0)).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {invoice.status === 'paid' && (
                      <div className="totals-row flex justify-between py-2 bg-green-50 px-2 -mx-2 rounded">
                        <span className="text-green-800 font-semibold">Balance Due</span>
                        <span className="font-bold text-green-800">$0.00</span>
                      </div>
                    )}
                  </>
                )}
                
                {/* Pending - No payments yet */}
                {invoice.status === 'pending' && !invoice.paidAmount && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="totals-row flex justify-between py-2 bg-yellow-50 px-2 -mx-2 rounded">
                      <span className="text-yellow-800 font-semibold">Amount Due</span>
                      <span className="font-bold text-yellow-800">${(invoice.grandTotal || 0).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Payment History */}
            {invoice.payments && invoice.payments.length > 0 && (
              <div className="payment-history mt-8 pt-6 border-t border-gray-200">
                <div className="text-sm font-semibold text-gray-700 mb-3">Payment History</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase">
                      <th className="py-2">Date</th>
                      <th className="py-2">Amount</th>
                      <th className="py-2">Method</th>
                      <th className="py-2">Memo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.payments.map((payment, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        <td className="py-2">
                          {payment.paymentDate ? format(new Date(payment.paymentDate), 'MMM dd, yyyy') : '-'}
                        </td>
                        <td className="py-2 text-green-600 font-medium">${(payment.amount || 0).toFixed(2)}</td>
                        <td className="py-2 capitalize">{payment.paymentMode?.replace('_', ' ') || '-'}</td>
                        <td className="py-2 text-gray-500">{payment.memo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer */}
            <div className="footer mt-10 pt-6 border-t border-gray-200 text-center text-gray-400 text-sm">
              Thank you for your business!
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceViewDialog;
